import React, { useState, useEffect } from 'react';
import { usePensionStore } from './store/pensionStore.ts';
import { YearCard } from './components/YearCard.tsx';
import { Header } from './components/Header.tsx';
import { AddYearModal } from './components/AddYearModal.tsx';
import { PWAInstallPrompt } from './components/PWAInstallPrompt.tsx';

function App() {
  const { getSortedYears } = usePensionStore();
  const [isAddYearModalOpen, setIsAddYearModalOpen] = useState(false);
  const [currentYearIndex, setCurrentYearIndex] = useState(0);
  const sortedYears = getSortedYears();
  
  // Find current tax year and set as default
  useEffect(() => {
    const currentYear = new Date().getFullYear();
    const currentYearIdx = sortedYears.findIndex(year => year.id === currentYear);
    if (currentYearIdx !== -1) {
      setCurrentYearIndex(currentYearIdx);
    }
  }, [sortedYears]);
  
  const handlePrevYear = () => {
    setCurrentYearIndex((prev) => Math.max(0, prev - 1));
  };
  
  const handleNextYear = () => {
    setCurrentYearIndex((prev) => Math.min(sortedYears.length - 1, prev + 1));
  };
  
  const currentYear = sortedYears[currentYearIndex];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header onAddYear={() => setIsAddYearModalOpen(true)} />
      
      <main className="flex-1 flex flex-col">
        {sortedYears.length === 0 ? (
          <div className="flex-1 flex items-center justify-center px-4">
            <div className="text-center">
              <p className="text-gray-500 mb-4">No years added yet</p>
              <button
                onClick={() => setIsAddYearModalOpen(true)}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                Add Your First Year
              </button>
            </div>
          </div>
        ) : (
          <div className="flex-1 flex flex-col">
            {/* Navigation Indicator */}
            <div className="flex items-center justify-center py-4 px-4">
              <div className="flex items-center gap-3">
                <button
                  onClick={handlePrevYear}
                  disabled={currentYearIndex === 0}
                  className={`p-2 rounded-full transition-colors ${
                    currentYearIndex === 0 
                      ? 'text-gray-300 cursor-not-allowed' 
                      : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                  }`}
                  aria-label="Previous year"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                
                <div className="flex items-center gap-2">
                  {sortedYears.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentYearIndex(index)}
                      className={`w-2 h-2 rounded-full transition-all ${
                        index === currentYearIndex 
                          ? 'bg-blue-600 w-6' 
                          : 'bg-gray-300 hover:bg-gray-400'
                      }`}
                      aria-label={`Go to year ${sortedYears[index].taxYear}`}
                    />
                  ))}
                </div>
                
                <button
                  onClick={handleNextYear}
                  disabled={currentYearIndex === sortedYears.length - 1}
                  className={`p-2 rounded-full transition-colors ${
                    currentYearIndex === sortedYears.length - 1 
                      ? 'text-gray-300 cursor-not-allowed' 
                      : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                  }`}
                  aria-label="Next year"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Current Year Card */}
            <div className="flex-1 px-4 pb-4">
              <YearCard year={currentYear} />
            </div>
          </div>
        )}
      </main>

      <AddYearModal
        isOpen={isAddYearModalOpen}
        onClose={() => setIsAddYearModalOpen(false)}
      />

      <PWAInstallPrompt />
    </div>
  );
}

export default App;
